/**
 * Guided colors + fonts editor with a live sample preview (AB#624).
 *
 * The approachable layer on top of the raw file editor (#482): instead of hand-
 * editing CSS, this reads the site's forked-theme `static/style.css`, surfaces its
 * `:root` `--color-*` / `--font-*` custom properties as pickers + font selects, and
 * writes the edited values back through the existing site theme-file API. A live
 * mini-page renders the current palette + fonts before you save.
 *
 * Scope note (AB#650): it edits whatever `FileSource` it is handed — the site's
 * own fork or an account/user blueprint — through the matching file API.
 */

import { useRef, useState, useEffect, type CSSProperties } from 'react';
import { resolveMediaUrl, authenticatedMediaUrl, fetchMediaBytes, useAuthStore } from '@wordpuppi/shared';
import { useSourceFile, useSourceSaveFile, type FileSource } from './theme/blueprint';
import {
  FONT_EXTS,
  fontFaceRule,
  fontFamilyFromFile,
  fontStackFor,
  parseFontFamilies,
  sanitizeFontFile,
} from './theme/themeFonts';
import { MediaBrowser } from './MediaBrowser';
import { pickFilesDesktop } from '@/utils/desktopActions';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  parseRootVars,
  applyRootVars,
  isColorVar,
  isFontVar,
  toHex6,
  type PaletteMode,
} from './themeVars';

/** `accept` for the font file pickers (web input + desktop dialog filter). */
const FONT_ACCEPT = FONT_EXTS.map((e) => '.' + e).join(',');

/** Curated font stacks offered in the font selects. */
const FONT_STACKS: { label: string; value: string }[] = [
  { label: 'System sans', value: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" },
  { label: 'System mono', value: "ui-monospace, 'SF Mono', Menlo, Monaco, monospace" },
  { label: 'Serif', value: "Georgia, 'Times New Roman', serif" },
  { label: 'Humanist sans', value: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif" },
  { label: 'Geometric sans', value: "'Avenir Next', Avenir, 'Century Gothic', sans-serif" },
  { label: 'Rounded', value: "ui-rounded, 'Hiragino Maru Gothic ProN', 'Quicksand', sans-serif" },
  { label: 'Slab serif', value: "'Rockwell', 'Roboto Slab', 'Courier New', serif" },
];

/** `--color-link-hover` → "Link hover" (drops the category prefix). */
function humanize(name: string): string {
  const parts = name.replace(/^--/, '').split('-').filter(Boolean);
  if (parts[0] === 'color' || parts[0] === 'font') parts.shift();
  const s = parts.join(' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Blob -> base64 payload for the theme-file API (data: prefix stripped). */
function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read that file'));
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.readAsDataURL(blob);
  });
}

const emptyEdits = (): Record<PaletteMode, Record<string, string>> => ({ light: {}, dark: {} });
const varsToMap = (vars: { name: string; value: string }[] | undefined): Record<string, string> =>
  Object.fromEntries((vars ?? []).map((v) => [v.name, v.value]));

export function ThemeStyleEditor({
  source,
  stylePath,
  siteSlug,
}: {
  source: FileSource;
  stylePath: string;
  /** Whose Media library the "From media…" font picker browses. */
  siteSlug: string;
}) {
  const actor = useAuthStore((state) => state.user?.pid ?? '');
  const mediaReads = useRef(new Set<AbortController>());
  const mediaEpoch = useRef(0);
  const sourceKey = JSON.stringify(source);
  useEffect(() => { mediaEpoch.current++; return () => { mediaEpoch.current++; for (const request of mediaReads.current) request.abort(); mediaReads.current.clear(); }; }, [actor, siteSlug, sourceKey]);
  const fileQuery = useSourceFile(source, stylePath);
  const saveFile = useSourceSaveFile(source);
  const [mode, setMode] = useState<PaletteMode>('light');
  const [editsByMode, setEditsByMode] =
    useState<Record<PaletteMode, Record<string, string>>>(emptyEdits);
  const [fontDialogOpen, setFontDialogOpen] = useState(false);
  const [addingFont, setAddingFont] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const css = fileQuery.data?.data?.content ?? '';
  // #628: the site palette has two modes — the light `:root` and the dark
  // `@media (prefers-color-scheme: dark)` `:root`. Edit whichever the segmented
  // control selects; a visitor's device setting picks one at view time.
  const lightBlock = parseRootVars(css, 'light');
  const darkBlock = parseRootVars(css, 'dark');
  const hasDark = darkBlock !== null;
  const block = mode === 'dark' ? darkBlock : lightBlock;

  const lightOrig = varsToMap(lightBlock?.vars);
  const darkOrig = varsToMap(darkBlock?.vars);
  const original = mode === 'dark' ? darkOrig : lightOrig;
  const edits = editsByMode[mode];

  const value = (name: string): string => (name in edits ? edits[name] : original[name]) ?? '';
  const setVar = (name: string, v: string) =>
    setEditsByMode((e) => ({ ...e, [mode]: { ...e[mode], [name]: v } }));
  const dirtyIn = (m: PaletteMode, orig: Record<string, string>) =>
    Object.entries(editsByMode[m]).some(([n, v]) => v !== (orig[n] ?? ''));
  const dirty = dirtyIn('light', lightOrig) || dirtyIn('dark', darkOrig);

  const colorVars = (block?.vars ?? []).filter((v) => isColorVar(v.name));
  const fontVars = (block?.vars ?? []).filter((v) => isFontVar(v.name));
  const editable =
    [...(lightBlock?.vars ?? []), ...(darkBlock?.vars ?? [])].some(
      (v) => isColorVar(v.name) || isFontVar(v.name),
    );

  // Fonts the blueprint already carries, i.e. every @font-face in this stylesheet.
  const customFamilies = parseFontFamilies(css);

  /**
   * Land a font in the blueprint: bytes to `static/fonts/<file>`, then an
   * `@font-face` rule prepended to the stylesheet. Two writes, because the file
   * API takes one file at a time. Pending palette edits live in `editsByMode`
   * and survive the refetch this triggers, so they are never touched here.
   */
  const addFont = async (name: string, blob: Blob) => {
    const file = sanitizeFontFile(name);
    if (!file) {
      toast.error(`Not a font file — use ${FONT_EXTS.map((e) => '.' + e).join(', ')}`);
      return;
    }
    const family = fontFamilyFromFile(name);
    setAddingFont(true);
    try {
      await saveFile.mutateAsync({
        path: `static/fonts/${file}`,
        content: await toBase64(blob),
        encoding: 'base64',
        expectedContent: '',
      });
      if (!customFamilies.includes(family)) {
        const hash = fileQuery.data?.data?.content_sha256;
        if (!hash) throw new Error('Reload the stylesheet before editing it.');
        await saveFile.mutateAsync({ path: stylePath, content: fontFaceRule(file, family) + css, expectedContent: hash });
      }
      toast.success(`Added "${family}" — pick it in a font select, then save`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not add that font');
    } finally {
      setAddingFont(false);
    }
  };

  const addFromMedia = async (url: string, alt?: string) => {
    setFontDialogOpen(false);
    const generation = mediaEpoch.current;
    const request = new AbortController(); mediaReads.current.add(request);
    try {
      if (authenticatedMediaUrl(url)) {
        const { bytes, contentType } = await fetchMediaBytes(url, request.signal);
        if (generation !== mediaEpoch.current) return;
        await addFont(alt || url, new Blob([new Uint8Array(bytes)], { type: contentType }));
      } else {
        const res = await fetch(resolveMediaUrl(url), { signal: request.signal, credentials: 'omit' });
        if (!res.ok) throw new Error(`Could not read that file (${res.status})`);
        const blob = await res.blob();
        if (generation !== mediaEpoch.current) return;
        await addFont(alt || url, blob);
      }
    } catch {
      if (generation === mediaEpoch.current) toast.error('Could not read that font file.');
    } finally { mediaReads.current.delete(request); }
  };

  const handleSave = async () => {
    if (!dirty) return;
    try {
      const hash = fileQuery.data?.data?.content_sha256;
      if (!hash) throw new Error('Reload the stylesheet before editing it.');
      // Splice each palette's block independently; byte-preserving so untouched
      // CSS (and the other palette) stays exactly as authored.
      let content = applyRootVars(css, editsByMode.light, 'light');
      content = applyRootVars(content, editsByMode.dark, 'dark');
      await saveFile.mutateAsync({ path: stylePath, content, expectedContent: hash });
      setEditsByMode(emptyEdits());
      toast.success('Colors & fonts saved — rebuild the site to apply');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Save failed');
    }
  };

  // Sample resolves against the selected mode: the light palette as a base,
  // overlaid with the dark overrides when the Dark tab is active.
  const previewValue = (m: PaletteMode, name: string): string => {
    const e = editsByMode[m];
    const orig = m === 'dark' ? darkOrig : lightOrig;
    return (name in e ? e[name] : orig[name]) ?? '';
  };
  const previewEntries = (lightBlock?.vars ?? []).map(
    (v) => [v.name, previewValue('light', v.name)] as const,
  );
  const darkEntries =
    mode === 'dark'
      ? (darkBlock?.vars ?? []).map((v) => [v.name, previewValue('dark', v.name)] as const)
      : [];
  const previewVars = Object.fromEntries([
    ...previewEntries,
    ...darkEntries,
  ]) as unknown as CSSProperties;

  return (
    <Card className="mb-4">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>Colors &amp; fonts</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditsByMode(emptyEdits())}
              disabled={!dirty}
            >
              Reset
            </Button>
            <Button size="sm" onClick={handleSave} disabled={!dirty || saveFile.isPending}>
              {saveFile.isPending ? 'Saving…' : 'Save colors & fonts'}
            </Button>
          </div>
        </div>
        <CardDescription>
          Edit this theme&apos;s palette and fonts, then rebuild (Preview / Publish) to apply. Your
          site has a light and a dark palette — each visitor&apos;s device setting picks one. For
          anything deeper, edit the files below.
          {editable && !hasDark && (
            <span className="mt-1 block">This theme uses one palette for both light and dark.</span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {fileQuery.isLoading ? (
          <Skeleton className="h-56 w-full rounded-lg" />
        ) : !editable ? (
          <p className="text-sm text-muted-foreground">
            This theme&apos;s stylesheet doesn&apos;t expose editable color or font variables. Use
            the file editor below to change its styles.
          </p>
        ) : (
          <div className="grid gap-6 @3xl:grid-cols-2">
            {/* Controls */}
            <div className="space-y-6">
              {hasDark && (
                <div
                  role="tablist"
                  aria-label="Palette mode"
                  className="inline-flex rounded-md border p-0.5"
                >
                  {(['light', 'dark'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      role="tab"
                      aria-selected={mode === m}
                      onClick={() => setMode(m)}
                      className={cn(
                        'rounded px-3 py-1 text-sm font-medium capitalize transition-colors',
                        mode === m
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              )}
              {colorVars.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium">Colors</h3>
                  {colorVars.map((v) => {
                    const val = value(v.name);
                    const hex = toHex6(val);
                    return (
                      <div key={v.name} className="flex items-center gap-3">
                        <Label htmlFor={`c-${v.name}`} className="w-28 shrink-0 text-sm break-words">
                          {humanize(v.name)}
                        </Label>
                        <input
                          id={`c-${v.name}`}
                          type="color"
                          value={hex ?? '#000000'}
                          onChange={(e) => setVar(v.name, e.target.value)}
                          className="h-9 w-10 shrink-0 cursor-pointer rounded-md border bg-transparent p-0.5"
                          aria-label={`${humanize(v.name)} color`}
                        />
                        <Input
                          value={val}
                          onChange={(e) => setVar(v.name, e.target.value)}
                          className="min-w-0 font-mono text-xs"
                          spellCheck={false}
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              {fontVars.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-medium">Fonts</h3>
                  {fontVars.map((v) => {
                    const val = value(v.name);
                    const custom = customFamilies.map((f) => ({
                      label: f,
                      value: fontStackFor(f),
                    }));
                    const known = [...custom, ...FONT_STACKS];
                    const options =
                      val && !known.some((f) => f.value === val)
                        ? [{ label: 'Theme default', value: val }, ...known]
                        : known;
                    return (
                      <div key={v.name} className="flex items-center gap-3">
                        <Label htmlFor={`f-${v.name}`} className="w-28 shrink-0 text-sm break-words">
                          {humanize(v.name)}
                        </Label>
                        <Select value={val} onValueChange={(nv) => setVar(v.name, nv as string)}>
                          <SelectTrigger id={`f-${v.name}`} className="w-full min-w-0">
                            <SelectValue placeholder="Choose a font">
                              {(sv: unknown) =>
                                options.find((o) => o.value === sv)?.label ?? 'Custom'
                              }
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {custom.length > 0 && (
                              <SelectGroup>
                                <SelectLabel>Your fonts</SelectLabel>
                                {custom.map((o) => (
                                  <SelectItem key={o.value} value={o.value}>
                                    <span style={{ fontFamily: o.value }}>{o.label}</span>
                                  </SelectItem>
                                ))}
                              </SelectGroup>
                            )}
                            {options
                              .filter((o) => !custom.some((c) => c.value === o.value))
                              .map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                  <span style={{ fontFamily: o.value }}>{o.label}</span>
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                  {hasDark && (
                    <p className="text-xs text-muted-foreground">Fonts apply to both palettes.</p>
                  )}

                  <div className="space-y-2 rounded-md border border-dashed p-3">
                    <p className="text-xs text-muted-foreground">
                      Custom fonts:{' '}
                      {customFamilies.length ? customFamilies.join(', ') : 'none yet'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <input
                        ref={fileInput}
                        type="file"
                        accept={FONT_ACCEPT}
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = '';
                          if (f) void addFont(f.name, f);
                        }}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={addingFont}
                        // AB#635: the Tauri webview never opens a native panel — desktop picks via the dialog plugin.
                        onClick={(e) => {
                          if (
                            !pickFilesDesktop(e, { accept: FONT_ACCEPT }, (files) => {
                              const f = files[0];
                              if (f) void addFont(f.name, f);
                            })
                          )
                            fileInput.current?.click();
                        }}
                      >
                        {addingFont ? 'Adding…' : 'Upload font…'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={addingFont}
                        onClick={() => setFontDialogOpen(true)}
                      >
                        From media…
                      </Button>
                    </div>
                  </div>

                  <Dialog open={fontDialogOpen} onOpenChange={setFontDialogOpen}>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Choose a font from Media</DialogTitle>
                      </DialogHeader>
                      <MediaBrowser
                        siteSlug={siteSlug}
                        kind="font"
                        onSelect={(url, alt) => void addFromMedia(url, alt)}
                        onCancel={() => setFontDialogOpen(false)}
                      />
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </div>

            {/* Live sample */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium">Live sample — {mode === 'dark' ? 'Dark' : 'Light'}</h3>
              {colorVars.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {colorVars.map((v) => (
                    <div key={v.name} className="text-center">
                      <div
                        className="h-8 w-8 rounded-md border"
                        style={{ background: value(v.name) }}
                        title={`${humanize(v.name)}: ${value(v.name)}`}
                      />
                      <div className="mt-1 w-12 truncate text-[10px] text-muted-foreground">
                        {humanize(v.name)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div style={previewVars} className="overflow-hidden rounded-lg border">
                <div
                  style={{
                    background: 'var(--color-bg, #ffffff)',
                    color: 'var(--color-text, #111827)',
                    fontFamily: 'var(--font-sans, system-ui, sans-serif)',
                    padding: '1.25rem',
                  }}
                >
                  <div
                    style={{
                      color: 'var(--color-text-muted, #6b7280)',
                      fontSize: 11,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      fontFamily: 'var(--font-mono, var(--font-sans, system-ui))',
                    }}
                  >
                    Sample article
                  </div>
                  <h3 style={{ margin: '6px 0 8px', fontSize: 22, lineHeight: 1.2 }}>
                    The quick brown fox
                  </h3>
                  <p style={{ margin: '0 0 10px', lineHeight: 1.6 }}>
                    Body copy set in the theme&apos;s base font. It shows how text,{' '}
                    <a
                      href="#preview"
                      onClick={(e) => e.preventDefault()}
                      style={{ color: 'var(--color-link, #2563eb)' }}
                    >
                      a link
                    </a>
                    , and muted notes read against the background.
                  </p>
                  <p
                    style={{
                      margin: '0 0 14px',
                      fontSize: 13,
                      color: 'var(--color-text-muted, #6b7280)',
                    }}
                  >
                    Muted secondary text for captions and metadata.
                  </p>
                  <button
                    type="button"
                    style={{
                      background: 'var(--color-link, #2563eb)',
                      color: 'var(--color-bg, #ffffff)',
                      border: 'none',
                      borderRadius: 8,
                      padding: '8px 14px',
                      fontFamily: 'inherit',
                      fontSize: 14,
                      cursor: 'default',
                    }}
                  >
                    Read more
                  </button>
                  <hr
                    style={{
                      border: 'none',
                      borderTop: '1px solid var(--color-border, #e5e7eb)',
                      margin: '14px 0 0',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
